import React, {useState} from 'react';
import {
  TableContainer,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Switch,
  CircularProgress,
  Paper,
  Button,
  DialogContent,
  DialogActions,
  Dialog,
  DialogTitle,
  FormControlLabel,
  TextField,
  Link,
  Breadcrumbs,
} from '@mui/material';
import {gql} from '@apollo/client';
import {useMutationCoordinate, useQueryCoordinate} from '../../../../clients/graphql/graphql.hooks';
import Box from '@mui/material/Box';
import {Link as RouterLink} from 'react-router-dom';

const ALL_FEATURE_FLAGS_QUERY = gql`
  query JediAllFeatureFlagsGet {
    jediAllFeatureFlagsGet {
      featureFlags {
        name
        isEnabled
        createdAt
        lastUpdatedAt
        lastUpdatedBy
      }
    }
  }
`;

const UPDATE_FEATURE_FLAG_MUTATION = gql`
  mutation JediFeatureFlagUpdate($name: String!, $newValue: Boolean!) {
    jediFeatureFlagUpdate(input: { name: $name, newValue: $newValue }) {
      success
      newValue
      name
      errorMsg
    }
  }
`;

const ADD_FEATURE_FLAG_MUTATION = gql`
mutation JediAddFeatureFlag ($name: String!, $value: Boolean!) {
  jediFeatureFlagAdd(input: { name: $name, isEnabled: $value }) {
    name
    isEnabled
    createdAt
    lastUpdatedAt
  }
}`;

export const AllFeatureFlagsPage: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [flagName, setFlagName] = useState('');
  const [enabled, setEnabled] = useState(false);

  const [addFlagMutation] = useMutationCoordinate(ADD_FEATURE_FLAG_MUTATION, {
    variables: { name: flagName, value: enabled }
  });

  const handleSubmit = () => {
    addFlagMutation()
    setFlagName('');
    setEnabled(false);
    setOpen(false);
  };


  const { loading, error, data } = useQueryCoordinate(ALL_FEATURE_FLAGS_QUERY, {});

  if (loading) return <CircularProgress />;
  if (error) return <p>Error loading feature flags</p>;

  return (
    <>
      <Breadcrumbs sx={{marginLeft: '15rem'}}>
        <Link component={RouterLink} to="/developer" color="inherit">
          Developer
        </Link>
        <Link
          component={RouterLink}
          to="/developer/release"
          color="inherit"
        >
          Release
        </Link>
      </Breadcrumbs>
    <TableContainer component={Paper} sx={{ maxWidth: 800, margin: 'auto', mt: 4 }}>
      <Box
        sx={{
          mt: 2,
          mb: 2,
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5">Feature Flags</Typography>
        <Button variant="outlined" onClick={() => setOpen(true)}>
          Add
        </Button>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Last Updated</TableCell>
            <TableCell>Last Updated By</TableCell>
            <TableCell align="center">Switch</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.jediAllFeatureFlagsGet.featureFlags.map((flag: any) => (
            <FlagToggleRow key={flag.name} flag={flag} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>

  <Dialog open={open} onClose={() => setOpen(false)}>
    <DialogTitle>Add Feature Flag</DialogTitle>
    <DialogContent>
      <TextField
        label="Flag Name"
        fullWidth
        margin="normal"
        value={flagName}
        onChange={(e) => setFlagName(e.target.value)}
      />
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        }
        label="Enabled"
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setOpen(false)}>Cancel</Button>
      <Button onClick={handleSubmit} variant="contained" disabled={!flagName.trim()}>
        Create
      </Button>
    </DialogActions>
  </Dialog>
    </>
  );
};

const FlagToggleRow: React.FC<{
  flag: { name: string; isEnabled: boolean; createdAt: string; lastUpdatedAt: string, lastUpdatedBy: string };
}> = ({ flag }) => {
  const [updateFlagMutation] = useMutationCoordinate(UPDATE_FEATURE_FLAG_MUTATION, {
    variables: { name: flag.name, newValue: !flag.isEnabled }
  });

  const handleToggle = async () => {
    try {
      const result = await updateFlagMutation();

      if (!result?.data?.jediFeatureFlagUpdate?.success) {
        alert(result?.data?.jediFeatureFlagUpdate?.errorMsg || 'Update failed');
      }
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert('Error updating flag');
    }
  };

  return (
    <TableRow>
      <TableCell>{flag.name}</TableCell>
      <TableCell>{new Date(flag.createdAt).toLocaleDateString()}</TableCell>
      <TableCell>{new Date(flag.lastUpdatedAt).toLocaleString()}</TableCell>
      <TableCell>{flag.lastUpdatedBy}</TableCell>
      <TableCell align="center">
        <Switch
          checked={flag.isEnabled}
          onChange={handleToggle}
          color="primary"
        />
      </TableCell>
    </TableRow>
  );
};